/**
 * Inventory Panel — 2x6 grid showing carried items and heals.
 * DOM-based UI component with teal/cyan theme.
 */

import type { GameState, ItemKey, CarryableObjectState, CarryableType } from '../types';
import { HEAL_ITEMS, HEAL_MOVEMENT_COST } from '../constants';
import { getPlayerCarrying } from '../core/carryables';

// =============================================================================
// TYPES
// =============================================================================

export interface InventoryCallbacks {
  onDropCarryable: (carryableId: number) => void;
  onUseHeal: (itemKey: ItemKey) => void;
  onDismiss: () => void;
}

// =============================================================================
// VISIBILITY
// =============================================================================

export function showInventoryPanel(show: boolean): void {
  const panel = document.getElementById('inventory-overlay');
  if (panel) panel.classList.toggle('hidden', !show);
}

export function isInventoryPanelVisible(): boolean {
  const panel = document.getElementById('inventory-overlay');
  return panel ? !panel.classList.contains('hidden') : false;
}

// =============================================================================
// ITEMS BUTTON
// =============================================================================

export function setupInventoryButton(onToggle: () => void): () => void {
  const btn = document.getElementById('inventory-btn');
  if (!btn) return () => {};
  btn.addEventListener('click', onToggle);
  return () => btn.removeEventListener('click', onToggle);
}

// =============================================================================
// CARRYABLE ICONS
// =============================================================================

const CARRYABLE_ICONS: Record<CarryableType, string> = {
  navCore: '\u{1F9ED}',       // compass
  dataFragment: '\u{1F4BE}',  // floppy disk
  salvageNode: '\u2699\uFE0F', // gear
  artifact: '\u{1F3FA}',      // amphora
  techSalvage: '\u{1F527}',   // wrench
};

const CARRYABLE_LABELS: Record<CarryableType, string> = {
  navCore: 'Nav Core',
  dataFragment: 'Data Frag',
  salvageNode: 'Salvage',
  artifact: 'Artifact',
  techSalvage: 'Tech Salv',
};

// =============================================================================
// PANEL CONTENT
// =============================================================================

export function updateInventoryPanel(
  gameState: GameState,
  playerId: number,
  callbacks: InventoryCallbacks
): void {
  const container = document.getElementById('inventory-grid');
  if (!container) return;

  const player = gameState.players.find(p => p.id === playerId);
  if (!player) return;

  container.innerHTML = '';

  // --- Row 1: Carryable slots (6 columns) ---
  const carried = getPlayerCarrying(playerId, gameState.carryables);
  const carryableTypes: CarryableType[] = ['navCore', 'artifact', 'dataFragment', 'salvageNode', 'techSalvage'];

  for (let i = 0; i < 6; i++) {
    const slot = document.createElement('div');
    slot.className = 'inventory-slot';

    if (i === 0 && carried) {
      // Show carried item in first slot
      slot.classList.add('occupied');
      renderCarryableSlot(slot, carried, callbacks);
    } else if (i > 0 && i <= carryableTypes.length) {
      // Empty placeholder with type label
      const label = document.createElement('div');
      label.className = 'inventory-slot-label';
      label.textContent = '—';
      label.style.opacity = '0.2';
      slot.appendChild(label);
    } else {
      // Blank slot
      const label = document.createElement('div');
      label.className = 'inventory-slot-label';
      label.textContent = '—';
      label.style.opacity = '0.2';
      slot.appendChild(label);
    }

    container.appendChild(slot);
  }

  // --- Row 2: Consumable slots (6 columns) ---
  const healKeys: ItemKey[] = ['healSmall', 'healLarge'];

  for (let i = 0; i < 6; i++) {
    const slot = document.createElement('div');
    slot.className = 'inventory-slot';

    if (i < healKeys.length) {
      const itemKey = healKeys[i];
      const count = player.loadout.items?.[itemKey] ?? 0;

      if (count > 0) {
        slot.classList.add('occupied');
        renderHealSlot(slot, itemKey, count, player.health, player.movementRemaining, callbacks);
      } else {
        // Show empty heal slot with label
        const def = HEAL_ITEMS[itemKey];
        const icon = document.createElement('div');
        icon.className = 'inventory-slot-icon';
        icon.textContent = itemKey === 'healSmall' ? '\u271A' : '\u271B';
        icon.style.opacity = '0.2';
        slot.appendChild(icon);

        const label = document.createElement('div');
        label.className = 'inventory-slot-label';
        label.textContent = def.name;
        label.style.opacity = '0.2';
        slot.appendChild(label);
      }
    } else {
      // Blank slot
      const label = document.createElement('div');
      label.className = 'inventory-slot-label';
      label.textContent = '—';
      label.style.opacity = '0.2';
      slot.appendChild(label);
    }

    container.appendChild(slot);
  }

  // Dismiss button
  const dismissBtn = document.createElement('button');
  dismissBtn.className = 'inventory-dismiss-btn';
  dismissBtn.textContent = 'DISMISS';
  dismissBtn.addEventListener('click', callbacks.onDismiss);
  container.parentElement!.appendChild(dismissBtn);

  // Remove any previous dismiss button (avoid duplicates)
  const existing = container.parentElement!.querySelectorAll('.inventory-dismiss-btn');
  if (existing.length > 1) {
    for (let i = 0; i < existing.length - 1; i++) {
      existing[i].remove();
    }
  }
}

// =============================================================================
// HELPERS
// =============================================================================

function renderCarryableSlot(
  slot: HTMLElement,
  carried: CarryableObjectState,
  callbacks: InventoryCallbacks
): void {
  const icon = document.createElement('div');
  icon.className = 'inventory-slot-icon';
  icon.textContent = CARRYABLE_ICONS[carried.type] ?? '?';
  slot.appendChild(icon);

  const label = document.createElement('div');
  label.className = 'inventory-slot-label';
  label.textContent = CARRYABLE_LABELS[carried.type] ?? carried.type;
  slot.appendChild(label);

  const dropBtn = document.createElement('button');
  dropBtn.className = 'inventory-action-btn';
  dropBtn.textContent = 'DROP';
  dropBtn.addEventListener('click', () => callbacks.onDropCarryable(carried.id));
  slot.appendChild(dropBtn);
}

function renderHealSlot(
  slot: HTMLElement,
  itemKey: ItemKey,
  count: number,
  health: number,
  movementRemaining: number,
  callbacks: InventoryCallbacks
): void {
  const def = HEAL_ITEMS[itemKey];

  const icon = document.createElement('div');
  icon.className = 'inventory-slot-icon';
  icon.textContent = itemKey === 'healSmall' ? '\u271A' : '\u271B';
  slot.appendChild(icon);

  const countEl = document.createElement('div');
  countEl.className = 'inventory-slot-count';
  countEl.textContent = `\u00D7${count}`;
  slot.appendChild(countEl);

  const label = document.createElement('div');
  label.className = 'inventory-slot-label';
  label.textContent = def.name;
  slot.appendChild(label);

  const useBtn = document.createElement('button');
  useBtn.className = 'inventory-action-btn use-btn';
  useBtn.textContent = 'USE';
  const canUse = health < 100 && movementRemaining >= HEAL_MOVEMENT_COST;
  useBtn.disabled = !canUse;
  useBtn.addEventListener('click', () => callbacks.onUseHeal(itemKey));
  slot.appendChild(useBtn);
}
